// frontend\src\components\Inputs.tsx
// Consumables consumed by shipping products. Admins get the full page; a
// warehouse user gets the inputs their warehouse uses (read-only) and can
// request more, which goes onto the MPP week list (RequestDialog).
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useTasks } from '../context/TasksContext';
import { Input, InputHealth, InputUsageRule } from '../types';
import { TaskItem } from '../types/tasks';
import { useTableSort, SortableTh } from '../hooks/useTableSort';
import LoadingSpinner from './LoadingSpinner';
import InputDialog from './InputDialog';
import InputsCoverChart from './InputsCoverChart';
import RequestDialog, { RequestPreset } from './tasks/RequestDialog';
import { firstName } from '../utils/taskUsers';
import '../core.css';
import '../dashboard.css';
import '../inputs.css';

// Maps input health onto the existing stock-health chip classes in core.css.
const HEALTH_CLASS: Record<InputHealth, string> = {
  out: 'stock-health-low',
  critical: 'stock-health-low',
  low: 'stock-health-medium',
  ok: 'stock-health-high',
  na: 'stock-health-na',
};

const HEALTH_LABEL: Record<InputHealth, string> = {
  out: 'Out',
  critical: 'Order now',
  low: 'Low',
  ok: 'OK',
  na: 'No usage',
};

/** Renders a rule as the same sentence the rule builder shows. */
const describeRule = (rule: InputUsageRule): string => {
  const qty = Number(rule.qty_per_unit);
  if (rule.scope === 'supplier') return `${qty} per ${rule.supplier_name} product`;
  if (rule.scope === 'product') return `${qty} per ${rule.product_name || 'product'}`;
  return `${qty} per product`;
};

/** Flattened row shape so useTableSort can sort on plain scalars. */
interface InputRow extends Input {
  usage_summary: string;
  stock_value: number;
}

/** Default restock quantity when the input has no reorder quantity set. */
const suggestedRestock = (input: Input): number =>
  input.reorder_qty ?? (input.burn_rate > 0 ? Math.ceil(input.burn_rate * 2) : 0);

const AdminInputs: React.FC = () => {
  const { showToast } = useToast();
  const [inputs, setInputs] = useState<Input[]>([]);
  const [warehouseNames, setWarehouseNames] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInput, setSelectedInput] = useState<Input | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [restockTarget, setRestockTarget] = useState<Input | null>(null);
  const [restockQty, setRestockQty] = useState('');
  const [restocking, setRestocking] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const fetchInputs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/inputs');
      setInputs(response.data.inputs || []);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load inputs:', err);
      setError(err?.response?.data?.error || 'Failed to load inputs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInputs();
  }, [fetchInputs]);

  // For the "Used by" line under each input's name.
  useEffect(() => {
    api
      .get('/warehouses')
      .then((r) => setWarehouseNames(new Map((r.data.warehouses || []).map((w: any) => [w.id, w.name]))))
      .catch((err) => console.error('Failed to load warehouses:', err));
  }, []);

  // Deep link from a task: /inputs?input=ID opens that input.
  useEffect(() => {
    const id = Number(new URLSearchParams(location.search).get('input'));
    if (!id || loading) return;
    const match = inputs.find((i) => i.id === id);
    if (match) {
      setSelectedInput(match);
      setCreatingNew(false);
      setDialogOpen(true);
    }
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, loading, inputs, navigate]);

  const rows = useMemo<InputRow[]>(
    () =>
      inputs.map((input) => ({
        ...input,
        usage_summary: input.rules.length === 0 ? 'No rules' : input.rules.map(describeRule).join(', '),
        stock_value: (Number(input.cost) || 0) * input.current_stock,
      })),
    [inputs]
  );

  // Auto-filters as characters are entered, same as the stock table.
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((row) =>
      [row.name, row.part_number || '', row.supplier_name || '', row.usage_summary]
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [rows, searchTerm]);

  const sort = useTableSort<InputRow>(filteredRows, 'days_cover', 'asc');

  const kpis = useMemo(() => {
    const needsOrder = inputs.filter((i) => i.health === 'critical' || i.health === 'out').length;
    const outOfStock = inputs.filter((i) => i.health === 'out').length;
    const lowSoon = inputs.filter((i) => i.health === 'low').length;
    const value = inputs.reduce((sum, i) => sum + (Number(i.cost) || 0) * Math.max(i.current_stock, 0), 0);
    return { needsOrder, outOfStock, lowSoon, value };
  }, [inputs]);

  // Stands in for the stock page's "Last Updated" — it is the useful signal
  // that the nightly consumption cron is actually running.
  const lastConsumed = useMemo(() => {
    const stamps = inputs
      .map((i) => i.last_consumed_at)
      .filter((s): s is string => Boolean(s))
      .sort();
    return stamps.length > 0 ? stamps[stamps.length - 1] : null;
  }, [inputs]);

  const kpiCards: { label: string; value: string; sub: string; alert?: boolean }[] = [
    { label: 'Tracked Inputs', value: String(inputs.length), sub: `${inputs.filter((i) => i.rules.length === 0).length} without usage rules` },
    { label: 'Order Now', value: String(kpis.needsOrder), sub: 'cover below lead time', alert: kpis.needsOrder > 0 },
    { label: 'Low Soon', value: String(kpis.lowSoon), sub: 'within 14 days of reorder point', alert: kpis.lowSoon > 0 },
    { label: 'Out of Stock', value: String(kpis.outOfStock), sub: 'at or below zero', alert: kpis.outOfStock > 0 },
    {
      label: 'Value On Hand',
      value: kpis.value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
      sub: 'at listed unit cost',
    },
  ];

  const openInput = (input: Input) => {
    setSelectedInput(input);
    setCreatingNew(false);
    setDialogOpen(true);
  };

  const openNew = () => {
    setSelectedInput(null);
    setCreatingNew(true);
    setDialogOpen(true);
  };

  // Refetching on close is how the rest of the app keeps lists fresh.
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInput(null);
    setCreatingNew(false);
    fetchInputs();
  };

  const openRestock = (input: Input) => {
    setRestockTarget(input);
    setRestockQty(String(suggestedRestock(input)));
  };

  // A restock is a positive adjustment, so it shows up in the input's Stock
  // Adjustments history rather than silently rebasing the manual count.
  const confirmRestock = async () => {
    if (!restockTarget) return;
    const qty = Number(restockQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      showToast('Enter a quantity greater than zero', 'error');
      return;
    }

    setRestocking(true);
    try {
      await api.post('/input-adjustments', {
        input_id: restockTarget.id,
        quantity: Math.round(qty),
        reason: 'Restock received',
      });
      showToast(`Received ${Math.round(qty)} × ${restockTarget.name}`, 'success');
      setRestockTarget(null);
      fetchInputs();
    } catch (err) {
      console.error('Failed to record restock:', err);
      showToast('Failed to record restock', 'error');
    } finally {
      setRestocking(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading inputs..." />;

  if (error) {
    return (
      <div className="dash-container">
        <div className="dash-error">
          Error: {error}{' '}
          <button className="dash-retry-button" onClick={fetchInputs}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-container inputs-container">
      <h1>MPP Inputs</h1>
      <p className="last-updated">
        {lastConsumed
          ? `Consumption last recorded: ${new Date(lastConsumed).toLocaleString('en-US')}`
          : 'No consumption recorded yet'}
      </p>

      <p className="dash-card-note inputs-intro">
        Consumables used up by shipping products — cards, lanyards, hardware kits, brackets.
        Stock is decremented nightly from what actually shipped.
      </p>

      <div className="dash-kpi-grid">
        {kpiCards.map((kpi) => (
          <div className={`dash-kpi-card ${kpi.alert ? 'alert' : ''}`} key={kpi.label}>
            <span className="dash-kpi-label">{kpi.label}</span>
            <span className="dash-kpi-value">{kpi.value}</span>
            <span className="dash-kpi-sub">{kpi.sub}</span>
          </div>
        ))}
      </div>

      <InputsCoverChart inputs={inputs} />

      <div className="search-container inputs-search">
        <input
          className="search-input"
          type="text"
          placeholder="Search inputs by name, part number, supplier or usage..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <span className="inputs-count">
          {sort.sorted.length} of {inputs.length}
        </span>
        <button className="action-button primary-button" onClick={openNew}>
          + New Input
        </button>
      </div>

      {inputs.length === 0 ? (
        <p className="dash-empty">
          No inputs yet. Create one and give it a usage rule to start tracking consumption.
        </p>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table inputs-table">
            <thead>
              <tr>
                <SortableTh<InputRow> label="Input" column="name" sort={sort} />
                <SortableTh<InputRow> label="Part #" column="part_number" sort={sort} />
                <SortableTh<InputRow> label="Supplier" column="supplier_name" sort={sort} />
                <SortableTh<InputRow> label="Usage" column="usage_summary" sort={sort} />
                <SortableTh<InputRow> label="Stock" column="current_stock" sort={sort} center />
                <SortableTh<InputRow> label="Burn / mo" column="burn_rate" sort={sort} center />
                <SortableTh<InputRow> label="Days Cover" column="days_cover" sort={sort} center />
                <SortableTh<InputRow> label="Lead" column="delivery_days" sort={sort} center />
                <SortableTh<InputRow> label="Order By" column="reorder_by" sort={sort} center />
                <SortableTh<InputRow> label="Cost" column="cost" sort={sort} center />
                <SortableTh<InputRow> label="Status" column="health" sort={sort} center />
                <th style={{ textAlign: 'center' }}>Restock</th>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row) => (
                <tr key={row.id} className="inputs-row" onClick={() => openInput(row)}>
                  <td className="inputs-name">
                    {row.name}
                    {(row.warehouse_ids || []).length > 0 && (
                      <span className="inputs-used-by">
                        Used by {row.warehouse_ids.map((id) => warehouseNames.get(id) || `#${id}`).join(', ')}
                      </span>
                    )}
                  </td>
                  <td>{row.part_number || '—'}</td>
                  <td>{row.supplier_name || '—'}</td>
                  <td className="inputs-usage">{row.usage_summary}</td>
                  <td style={{ textAlign: 'center' }}>{row.current_stock}</td>
                  <td style={{ textAlign: 'center' }}>
                    {row.burn_rate}
                    {row.burn_source === 'forecast' && (
                      <span className="inputs-est" title="Estimated from average sales — not enough consumption history yet">
                        {' '}
                        est.
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>{row.days_cover === null ? '—' : row.days_cover}</td>
                  <td style={{ textAlign: 'center' }}>{row.delivery_days + row.safety_days}</td>
                  <td style={{ textAlign: 'center' }}>{row.reorder_by || '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {row.cost === null || row.cost === undefined
                      ? '—'
                      : `${Number(row.cost).toFixed(2)} ${row.cost_currency || ''}`}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`stock-health-chip ${HEALTH_CLASS[row.health]}`}>
                      {HEALTH_LABEL[row.health]}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className="action-button inputs-restock-button"
                      title={`Record a delivery of ${row.name}`}
                      // The row itself opens the editor, so keep the click here.
                      onClick={(e) => {
                        e.stopPropagation();
                        openRestock(row);
                      }}
                    >
                      Restock
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {restockTarget && (
        <div className="supplier-dialog-overlay">
          <div className="supplier-dialog">
            <h3>Restock {restockTarget.name}</h3>
            <p className="supplier-dialog-subtitle">
              Confirm how many arrived. This is added to stock and logged in the input's Stock
              Adjustments history.
            </p>
            <label className="inputs-restock-field">
              Quantity received
              <input
                type="number"
                min="1"
                autoFocus
                value={restockQty}
                onChange={(e) => setRestockQty(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmRestock();
                  if (e.key === 'Escape') setRestockTarget(null);
                }}
              />
            </label>
            <p className="input-rules-hint">
              Current stock {restockTarget.current_stock} →{' '}
              {restockTarget.current_stock + (Number(restockQty) || 0)}
              {restockTarget.reorder_qty === null || restockTarget.reorder_qty === undefined
                ? ' (no reorder quantity set, suggested from burn rate)'
                : ''}
            </p>
            <div className="supplier-dialog-actions">
              <button
                className="action-button cancel-button"
                onClick={() => setRestockTarget(null)}
                disabled={restocking}
              >
                Cancel
              </button>
              <button
                className="action-button primary-button"
                onClick={confirmRestock}
                disabled={restocking}
              >
                {restocking ? 'Saving...' : 'Confirm Restock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {dialogOpen && (
        <InputDialog input={selectedInput} isNew={creatingNew} onClose={handleCloseDialog} />
      )}
    </div>
  );
};

// ── Warehouse view ──────────────────────────────────────────────────────────

/** What /api/inputs returns to a warehouse user: stock and status only. */
type WarehouseInput = Pick<
  Input,
  | 'id' | 'name' | 'part_number' | 'reorder_qty' | 'warehouse_ids' | 'current_stock' | 'burn_rate'
  | 'days_cover' | 'delivery_days' | 'safety_days' | 'reorder_by' | 'health'
>;

const WAREHOUSE_HEALTH_LABEL: Record<InputHealth, string> = {
  ...HEALTH_LABEL,
  critical: 'Very low',
};

const REQUESTS_SHOWN = 6;

const WarehouseInputs: React.FC = () => {
  const { ready: tasksReady, items, lists, users, me } = useTasks();
  const listTitle = useMemo(() => new Map(lists.map((l) => [l.id, l.title])), [lists]);
  const location = useLocation();
  const navigate = useNavigate();
  const [inputs, setInputs] = useState<WarehouseInput[]>([]);
  const [warehouses, setWarehouses] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [request, setRequest] = useState<RequestPreset | null>(null);

  const fetchInputs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/inputs');
      setInputs(response.data.inputs || []);
      setWarehouses(response.data.warehouses || []);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load inputs:', err);
      setError(err?.response?.data?.error || 'Failed to load inputs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInputs();
  }, [fetchInputs]);

  // Deep link from a request's input chip: /inputs?input=ID finds that row.
  useEffect(() => {
    const id = Number(new URLSearchParams(location.search).get('input'));
    if (!id || loading) return;
    const match = inputs.find((i) => i.id === id);
    if (match) setSearchTerm(match.name);
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, loading, inputs, navigate]);

  // My warehouse's requests (they're shared with everyone there), open first.
  // Week-list copies are the same request again.
  const myRequests = useMemo(
    () =>
      items
        .filter((i) => i.source === 'request' && !i.linked_to && i.depth === 0)
        .sort((a, b) => Number(a.progress >= 100) - Number(b.progress >= 100) || b.created_at.localeCompare(a.created_at)),
    [items]
  );
  const openRequestFor = useMemo(() => {
    const map = new Map<string, TaskItem>();
    myRequests.forEach((r) => {
      if (r.progress < 100 && r.link_type === 'input' && r.link_ref) map.set(r.link_ref, r);
    });
    return map;
  }, [myRequests]);

  const filtered = useMemo(() => {
    if (!searchTerm) return inputs;
    const term = searchTerm.toLowerCase();
    return inputs.filter((i) => `${i.name} ${i.part_number || ''}`.toLowerCase().includes(term));
  }, [inputs, searchTerm]);
  const sort = useTableSort<WarehouseInput>(filtered, 'days_cover', 'asc');

  const low = inputs.filter((i) => i.health === 'low' || i.health === 'critical').length;
  const out = inputs.filter((i) => i.health === 'out').length;
  const openCount = myRequests.filter((r) => r.progress < 100).length;
  const kpiCards: { label: string; value: string; sub: string; alert?: boolean }[] = [
    { label: 'Inputs', value: String(inputs.length), sub: 'used by your warehouse' },
    { label: 'Running Low', value: String(low), sub: 'worth requesting soon', alert: low > 0 },
    { label: 'Out of Stock', value: String(out), sub: 'at or below zero', alert: out > 0 },
    { label: 'Open Requests', value: String(openCount), sub: 'waiting on MPP' },
  ];

  const requestMore = (input: WarehouseInput) =>
    setRequest({
      title: `More ${input.name} needed`,
      lines: [input.reorder_qty ? `${input.reorder_qty}x ${input.name}` : input.name],
      inputId: input.id,
    });

  if (loading) return <LoadingSpinner message="Loading inputs..." />;

  if (error) {
    return (
      <div className="dash-container">
        <div className="dash-error">
          Error: {error}{' '}
          <button className="dash-retry-button" onClick={fetchInputs}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const where = warehouses.map((w) => w.name).join(' / ');

  return (
    <div className="dash-container inputs-container">
      <div className="dash-header">
        <h1>{where ? `${where} Inputs` : 'Inputs'}</h1>
        <button className="action-button primary-button" onClick={() => setRequest({})}>
          + Custom Request
        </button>
      </div>
      <p className="dash-card-note inputs-intro">
        Stock of the consumables your warehouse uses. Ask MPP for more with <strong>Request</strong> on a row, or send
        a <strong>Custom Request</strong> for anything else — you'll get an email when MPP leaves a note on it or
        completes it.
      </p>

      <div className="dash-kpi-grid">
        {kpiCards.map((kpi) => (
          <div className={`dash-kpi-card ${kpi.alert ? 'alert' : ''}`} key={kpi.label}>
            <span className="dash-kpi-label">{kpi.label}</span>
            <span className="dash-kpi-value">{kpi.value}</span>
            <span className="dash-kpi-sub">{kpi.sub}</span>
          </div>
        ))}
      </div>

      {tasksReady && myRequests.length > 0 && (
        <div className="dash-card">
          <div className="dash-card-header">
            <h3>Warehouse Requests</h3>
            <button type="button" className="dash-link inputs-link-btn" onClick={() => navigate('/lists')}>
              Go to Lists →
            </button>
          </div>
          <ul className="inputs-requests">
            {myRequests.slice(0, REQUESTS_SHOWN).map((r) => (
              <li key={r.id}>
                <button type="button" className="inputs-request" onClick={() => navigate(`/lists?item=${r.id}`)}>
                  <span className="inputs-request-text">
                    {listTitle.get(r.list_id) || r.text}
                    {r.created_by !== me && <span className="inputs-used-by">from {firstName(users, r.created_by)}</span>}
                  </span>
                  <span className={`dash-chip ${r.progress >= 100 ? 'ok' : r.progress > 0 || r.note ? 'warn' : 'neutral'}`}>
                    {r.progress >= 100 ? 'Completed' : r.progress > 0 || r.note ? 'In progress' : 'Sent'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="search-container inputs-search">
        <input
          className="search-input"
          type="text"
          placeholder="Search inputs by name or part number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <span className="inputs-count">
          {sort.sorted.length} of {inputs.length}
        </span>
      </div>

      {inputs.length === 0 ? (
        <p className="dash-empty">
          No inputs are marked as used by your warehouse yet. You can still send a Custom Request.
        </p>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table inputs-table">
            <thead>
              <tr>
                <SortableTh<WarehouseInput> label="Input" column="name" sort={sort} />
                <SortableTh<WarehouseInput> label="Part #" column="part_number" sort={sort} />
                <SortableTh<WarehouseInput> label="Stock" column="current_stock" sort={sort} center />
                <SortableTh<WarehouseInput> label="Used / mo" column="burn_rate" sort={sort} center />
                <SortableTh<WarehouseInput> label="Days Left" column="days_cover" sort={sort} center />
                <SortableTh<WarehouseInput> label="Status" column="health" sort={sort} center />
                <th style={{ textAlign: 'center' }}>Request</th>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row) => {
                const open = openRequestFor.get(String(row.id));
                return (
                  <tr key={row.id}>
                    <td className="inputs-name">{row.name}</td>
                    <td>{row.part_number || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{row.current_stock}</td>
                    <td style={{ textAlign: 'center' }}>{row.burn_rate}</td>
                    <td style={{ textAlign: 'center' }}>{row.days_cover === null ? '—' : row.days_cover}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`stock-health-chip ${HEALTH_CLASS[row.health]}`}>
                        {WAREHOUSE_HEALTH_LABEL[row.health]}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {open ? (
                        <button
                          className="action-button inputs-restock-button"
                          title="Your warehouse has already asked for this — open the request"
                          onClick={() => navigate(`/lists?item=${open.id}`)}
                        >
                          Requested ✓
                        </button>
                      ) : (
                        <button
                          className="action-button inputs-restock-button"
                          title={`Ask MPP for more ${row.name}`}
                          onClick={() => requestMore(row)}
                        >
                          Request
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {request && <RequestDialog warehouses={warehouses} preset={request} onClose={() => setRequest(null)} />}
    </div>
  );
};

const Inputs: React.FC = () => {
  const { permissions } = useAuth();
  return permissions?.user_type === 'Admin' ? <AdminInputs /> : <WarehouseInputs />;
};

export default Inputs;
